import { useEffect, useState, useRef } from "react";
import {
  motion,
  motionValue,
  useReducedMotion,
  useScroll,
  useTransform,
} from "framer-motion";

// images
import GooglePlayBtn from "../assets/images/google-playstore.png";
import AppStoreBtn from "../assets/images/app-store.png";
import BlackArrow from "../assets/images/black-arrow.png";

// texts
import Section1 from "./PinnedSections/ExamPass/Section1";
import Section2 from "./PinnedSections/ExamPass/Section2";
import Section3 from "./PinnedSections/ExamPass/Section3";
import Section4 from "./PinnedSections/ExamPass/Section4";
import Section5 from "./PinnedSections/ExamPass/Section5";
import Section6 from "./PinnedSections/ExamPass/Section6";

const ExamPassData = [
  {
    title: "Concise Lesson Notes",
    details:
      "Comprehensive, detailed, up-to-date & self-explanatory notes spanning all topics, concepts, theories, laws, equations, formulas, subjects and levels of education.",
  },
  {
    title: "Interactive Instructional Videos",
    details:
      "Enjoy the best of blended learning with our high-quality, interactive videos. Pause & play as you want and learn at your own pace. Learning can be fun too!",
  },
  {
    title: "Experienced Subject Teachers",
    details:
      "Our tutors are subject-matter experts with decades of experience in their niche.",
  },
  {
    title: "Online/Offline Mock Test (CBT)",
    details:
      "Get in top shape for examinations with access to over 40,000+ IGSCE, A-LEVELS, UTME, WAEC, BECE and Common Entrance Past questions and ready-made solutions.",
  },
  {
    title: "Quiz & Performance Analysis",
    details:
      "Assess your knowledge with our topic-related quizzes at the end of each lesson. Consolidate your strengths and get tips for necessary improvement.",
  },
  {
    title: "Tutor Live Chat",
    details:
      "Get help within seconds from expert tutors through our 24/7 Tutor Live Chat.",
  },
];

const imageVariant = {
  visible: {
    x: 0,
    opacity: 1,
    transition: { ease: "easeOut", duration: 0.4, delay: 0.1 },
  },
  leave: {
    x: 40,
    opacity: 0,
    transition: { ease: "easeIn", duration: 0.2 },
  },
};

const SECTIONS = [Section1, Section2, Section3, Section4, Section5, Section6];

const SECTIONSIMAGES = [
  "https://res.cloudinary.com/aonetutor-ltd/image/upload/f_auto,q_auto,c_limit,w_900/v1681187989/A1tutor%20website/revert5_dvtq6y.png",
  "https://res.cloudinary.com/aonetutor-ltd/image/upload/f_auto,q_auto,c_limit,w_900/v1672097602/A1tutor%20website/phone2_mpsgpr.png",
  "https://res.cloudinary.com/aonetutor-ltd/image/upload/f_auto,q_auto,c_limit,w_900/v1681188163/A1tutor%20website/revert1_uq0pmj.png",
  "https://res.cloudinary.com/aonetutor-ltd/image/upload/f_auto,q_auto,c_limit,w_900/v1672097602/A1tutor%20website/phone4_iyeoeb.png",
  "https://res.cloudinary.com/aonetutor-ltd/image/upload/f_auto,q_auto,c_limit,w_900/v1672099180/A1tutor%20website/class-attendance_pynwdy.png",
  "https://res.cloudinary.com/aonetutor-ltd/image/upload/f_auto,q_auto,c_limit,w_900/v1681188305/A1tutor%20website/revert4_qv2kn3.png",
];

const CARD_STICKY_TOP = 90;
const CARD_PEEK = 14;

const ExamPassCard = ({
  examData,
  image,
  index,
  coverProgress,
  previousCoverProgress,
}) => {
  const cardRef = useRef(null);
  const shouldReduceMotion = useReducedMotion();
  const top = CARD_STICKY_TOP + index * CARD_PEEK;
  const depth = ExamPassData.length - 1 - index;

  const { scrollYProgress } = useScroll({
    target: cardRef,
    offset: ["start 70%", `start ${top}px`],
  });

  useEffect(() => {
    if (!previousCoverProgress) return;
    previousCoverProgress.set(scrollYProgress.get());
    return scrollYProgress.onChange((progress) =>
      previousCoverProgress.set(progress)
    );
  }, [scrollYProgress, previousCoverProgress]);

  const scale = useTransform(coverProgress, [0, 1], [1, 1 - depth * 0.03]);
  const shade = useTransform(coverProgress, [0, 1], [0, depth * 0.03]);

  return (
    <motion.div
      ref={cardRef}
      className="exam-pass-card"
      style={{
        "--card-top": `${top}px`,
        scale: shouldReduceMotion ? 1 : scale,
      }}
    >
      <h5 className="mb-3 tx-blue">{examData.title}</h5>
      <p>{examData.details}</p>
      <div className="exam-pass-card-phone">
        <img src={image} alt="app phone" loading="lazy" />
      </div>
      <motion.div
        className="exam-pass-card-shade"
        style={{ opacity: shouldReduceMotion ? 0 : shade }}
      />
    </motion.div>
  );
};

const ExamPass = () => {
  const [activeSection, setActiveSection] = useState(0);
  const [coverProgress] = useState(() =>
    ExamPassData.map(() => motionValue(0))
  );
  const [downloadLink, setDownloadLink] = useState("");
  useEffect(() => {
    if (window.navigator.userAgent.match(/Android/i)) {
      // Device is an Android
      setDownloadLink(
        "https://play.google.com/store/apps/details?id=com.A1Tutor.a1_app"
      );
    } else if (window.navigator.userAgent.match(/iPhone|iPad|iPod/i)) {
      // Device is an iPhone, iPad, or iPod
      setDownloadLink("https://apps.apple.com/ng/app/a1tutor/id1595970443");
    } else if (window.navigator.userAgent.match(/IEMobile/i)) {
      // Device is a Windows Phone
      setDownloadLink(
        "https://play.google.com/store/apps/details?id=com.A1Tutor.a1_app"
      );
    } else {
      // Device is a desktop or laptop
      setDownloadLink(
        "https://play.google.com/store/apps/details?id=com.A1Tutor.a1_app"
      );
    }
  }, []);

  const pinnedRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: pinnedRef,
    offset: ["start start", "end end"],
  });

  useEffect(
    () =>
      scrollYProgress.onChange((progress) => {
        setActiveSection(
          Math.min(SECTIONS.length - 1, Math.floor(progress * SECTIONS.length))
        );
      }),
    [scrollYProgress]
  );

  return (
    <>
      <div
        ref={pinnedRef}
        className="d-none d-md-block"
        style={{ height: `${(SECTIONS.length + 1) * 100}vh` }}
      >
        <section
          id="exam-pass"
          className="container-fluid d-none d-md-flex exam-pass py-5 "
          style={{ height: "100vh", position: "sticky", top: 0 }}
        >
          <div
            className="container d-grid place-content-center"
            style={{ minHeight: "80vh" }}
          >
            <div className="row g-5">
              <div className="col-lg-7 col-md-6  my-xxl-auto">
                <div className="exam-pass-content">
                  <div className="my-5">
                    <h3 id="eve">
                      <span className="tx-blue">Everything</span> you need{" "}
                      <br className="d-none d-lg-block" /> to pass your exam
                    </h3>
                  </div>
                  <div className="col-md-12 col-lg-12">
                    <div
                      className="dynamic-text"
                      style={{ minHeight: "250px" }}
                    >
                      {SECTIONS.map((Section, i) => (
                        <Section key={i} isActive={activeSection === i} />
                      ))}
                    </div>

                    <div className="mb-3">
                      <a href={downloadLink} style={{ textDecoration: "none" }}>
                        <button
                          className="trans-btn mb-3 justify-content-center d-flex align-items-center"
                          style={{ gap: "5px" }}
                        >
                          <span>Download App</span>
                          <span>
                            <img
                              src={BlackArrow}
                              alt=""
                              style={{ width: "14px" }}
                            />
                          </span>
                        </button>
                      </a>
                      <div className="d-flex gap-1">
                        <a href="https://apps.apple.com/ng/app/a1tutor/id1595970443">
                          <img src={AppStoreBtn} alt="app store" />
                        </a>
                        <a href="https://play.google.com/store/apps/details?id=com.A1Tutor.a1_app">
                          <img src={GooglePlayBtn} alt="google play" />
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-lg-5 col-md-6 exam-pass-phone pt-5 d-flex align-items-center justify-content-md-center justify-content-start">
                <div className="my-auto text-center exam-pass-phone-stack">
                  {SECTIONSIMAGES.map((image, i) => (
                    <motion.img
                      key={image}
                      variants={imageVariant}
                      initial={false}
                      animate={activeSection === i ? "visible" : "leave"}
                      src={image}
                      alt="app phone"
                      aria-hidden={activeSection !== i}
                      loading="lazy"
                      style={{ width: "100%" }}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* small tablets */}
      <section className="container-fluid exam-pass py-5 d-md-none">
        <div className="container">
          <div className="row g-5">
            <div className="mx-auto">
              <div className="exam-pass-content ">
                <div className="mb-5">
                  <h3 id="eve" data-aos="fade-up">
                    <span className="tx-blue">Everything</span> you need{" "}
                    <br className="d-none d-md-block" /> to pass your exam
                  </h3>
                </div>

                <div className="exam-pass-cards">
                  {ExamPassData.map((examData, i) => (
                    <ExamPassCard
                      key={i}
                      index={i}
                      examData={examData}
                      image={SECTIONSIMAGES[i]}
                      coverProgress={coverProgress[i]}
                      previousCoverProgress={coverProgress[i - 1]}
                    />
                  ))}
                </div>

                <div className="my-5" data-aos="fade-up">
                  <a href={downloadLink} style={{ textDecoration: "none" }}>
                    <button
                      className="trans-btn justify-content-center d-flex align-items-center mb-3"
                      style={{ gap: "5px" }}
                    >
                      <span>Download App</span>
                      <span>
                        <img
                          src={BlackArrow}
                          alt=""
                          style={{ width: "15px" }}
                        />
                      </span>
                    </button>
                  </a>
                  <div className="d-flex gap-1">
                    <a href="https://apps.apple.com/ng/app/a1tutor/id1595970443">
                      <img
                        src={AppStoreBtn}
                        alt="app store"
                        className="app-btn"
                      />
                    </a>
                    <a href="https://play.google.com/store/apps/details?id=com.A1Tutor.a1_app">
                      <img
                        src={GooglePlayBtn}
                        alt="google play"
                        className="app-btn"
                      />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default ExamPass;
